/**
 * HealthConditionsCard — iter759 (Feb 2026)
 *
 * Structured UI for adding/removing health conditions with a canonical
 * dropdown list, severity, diagnosis date + notes. Writes to
 * db.health_conditions and immediately feeds BHI/BTRE/BPP/twins via
 * PatientHealthContext.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Stethoscope, Plus, Trash2, Loader2, CheckCircle2, X,
  ChevronDown, ShieldCheck, FileText, ExternalLink, FlaskConical, Sparkles,
} from "lucide-react";
import { emitHealthEvent } from "@/utils/healthEvents";
import { withScoreDelta } from "@/utils/scoreDelta";
import { toast } from "sonner";

const SEVERITY_LABELS_BG = {
  mild:          "лека",
  moderate:      "умерена",
  severe:        "тежка",
  clinical:      "клинично установена",
  self_reported: "самосъобщено",
};

const CATEGORY_LABELS_BG = {
  breast_benign:      "Доброкачествени на гърдата",
  breast_benign_male: "Мъжки състояния",
  endocrine:          "Ендокринна система",
  metabolic:          "Метаболитни",
  cardio:             "Сърдечно-съдови",
  autoimmune:         "Автоимунни",
  inflammation:       "Възпалителни",
  nutrition:          "Хранителни дефицити",
  genetic:            "Генетика / фамилна анамнеза",
  other:              "Други",
};

// iter912 — Audit trace: badge copy + accent per source-kind.
const SOURCE_META = {
  epicrisis:      { icon: Stethoscope, label: "Епикриза",           color: "amber",   verb: "От" },
  lab_analysis:   { icon: FlaskConical, label: "Лабораторно",        color: "sky",     verb: "От" },
  lab:            { icon: FlaskConical, label: "Лабораторно",        color: "sky",     verb: "От" },
  auto_diagnosis: { icon: Sparkles,     label: "AI auto-diagnosis",  color: "fuchsia", verb: "Изведено от" },
  onboarding:     { icon: FileText,     label: "Онбординг",          color: "blue",    verb: "От" },
  manual:         { icon: null,         label: "Ръчно",              color: "slate",   verb: "" },
};

const fmtDate = (d) => {
  if (!d) return "";
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    return dt.toLocaleDateString("bg-BG", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return String(d);
  }
};

export default function HealthConditionsCard() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDeepLinked = location.hash === "#health-conditions";
  const [canonical, setCanonical] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ condition_id: "", severity: "self_reported", diagnosed_at: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const loadCanonical = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/health-conditions/canonical`);
      setCanonical(data.conditions || []);
    } catch { setCanonical([]); }
  }, [API]);

  const loadMine = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/health-conditions`);
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [API]);

  useEffect(() => { loadCanonical(); loadMine(); }, [loadCanonical, loadMine]);

  const submit = async () => {
    if (!form.condition_id) {
      setErrorMsg("Изберете състояние");
      return;
    }
    setSaving(true);
    setErrorMsg(null);
    try {
      // iter761 — Impact-delta toast around the create.
      const { delta } = await withScoreDelta(
        API,
        () => axios.post(`${API}/health-conditions`, form),
        { kind: "add" }
      );
      setForm({ condition_id: "", severity: "self_reported", diagnosed_at: "", notes: "" });
      setShowForm(false);
      await loadMine();
      emitHealthEvent("conditions-changed", { added: [form.condition_id], delta });
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Грешка при запис");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      const item = items.find((x) => x.id === id);
      // iter761 — Impact-delta toast around the delete (kind='remove').
      const { delta } = await withScoreDelta(
        API,
        () => axios.delete(`${API}/health-conditions/${id}`),
        { kind: "remove" }
      );
      await loadMine();
      emitHealthEvent("conditions-changed", { removed: [item?.condition_id].filter(Boolean), delta });
    } catch { /* silent */ }
  };

  // iter830 — Promote an onboarding-origin diagnosis to structured
  // health_conditions with full severity/diagnosed_at/notes.
  const consolidate = async (item) => {
    const severity = window.prompt(
      "Тежест? (mild / moderate / severe / clinical)",
      "moderate",
    );
    if (!severity) return;
    const diagnosed_at = window.prompt("Кога е поставена диагнозата? (YYYY-MM-DD)", "") || "";
    const notes = window.prompt("Кратка бележка (по желание)?", "") || "";
    try {
      await axios.post(`${API}/health-conditions/consolidate/${item.condition_id}`, {
        severity, diagnosed_at, notes,
      });
      await loadMine();
      emitHealthEvent("conditions-changed", { consolidated: [item.condition_id] });
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Грешка при потвърждаване");
    }
  };

  // Group canonical by category for a nicer dropdown
  const grouped = canonical.reduce((acc, c) => {
    (acc[c.category] ||= []).push(c);
    return acc;
  }, {});

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <section id="health-conditions" data-testid="health-conditions-card" className={`p-5 rounded-2xl border ${cardCls}`}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            isDark ? "bg-red-900/60" : "bg-red-100"
          }`}>
            <Stethoscope className="w-5 h-5 text-red-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] uppercase font-bold tracking-widest mb-0.5 ${
              isDark ? "text-red-300" : "text-red-600"
            }`}>
              Заболявания и състояния
            </div>
            <h2 className={`text-base font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
              Моите заболявания
            </h2>
            <p className={`text-[12px] mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              Тези състояния се използват от <b>BHI</b>, <b>Breast Tissue Restoration</b>,
              <b> Prevention Program</b> и всичките ви <b>Digital Twins</b> за да персонализират препоръките.
            </p>
          </div>
        </div>
        {/* iter883 — Back-to-BHI button ALWAYS visible (was gated on
            #health-conditions hash which many entry points lose). */}
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate("/health-intelligence/breast");
            }
          }}
          data-testid="back-to-bhi-btn"
          className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold mr-2 ${
            isDark ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
          }`}
        >
          ← Обратно към BHI
        </button>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            data-testid="add-condition-btn"
            className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold ${
              isDark ? "bg-red-600 hover:bg-red-500 text-white" : "bg-red-600 hover:bg-red-700 text-white"
            }`}
          >
            <Plus className="w-3.5 h-3.5" />
            Добави
          </button>
        )}
      </div>

      {/* Add form */}
      {showForm && (
        <div className={`p-3 rounded-xl border mb-4 ${
          isDark ? "bg-slate-950/60 border-slate-700" : "bg-slate-50 border-slate-200"
        }`} data-testid="add-condition-form">
          <div className="grid md:grid-cols-2 gap-2 mb-2">
            <div className="relative">
              <label className={`text-[10px] font-bold uppercase ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                Състояние *
              </label>
              <select
                value={form.condition_id}
                onChange={(e) => setForm({ ...form, condition_id: e.target.value })}
                data-testid="condition-select"
                className={`w-full mt-1 px-2 py-1.5 rounded text-[12px] border ${
                  isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800"
                }`}
              >
                <option value="">-- изберете --</option>
                {Object.entries(grouped).map(([cat, list]) => (
                  <optgroup key={cat} label={CATEGORY_LABELS_BG[cat] || cat}>
                    {list.map((c) => (
                      <option key={c.id} value={c.id}>{c.name_bg}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className={`text-[10px] font-bold uppercase ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                Тежест
              </label>
              <select
                value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value })}
                data-testid="condition-severity"
                className={`w-full mt-1 px-2 py-1.5 rounded text-[12px] border ${
                  isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800"
                }`}
              >
                {Object.entries(SEVERITY_LABELS_BG).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-2 mb-2">
            <div>
              <label className={`text-[10px] font-bold uppercase ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                Дата на диагноза (по избор)
              </label>
              <input
                type="date"
                value={form.diagnosed_at}
                onChange={(e) => setForm({ ...form, diagnosed_at: e.target.value })}
                data-testid="condition-date"
                className={`w-full mt-1 px-2 py-1.5 rounded text-[12px] border ${
                  isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800"
                }`}
              />
            </div>
            <div>
              <label className={`text-[10px] font-bold uppercase ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                Бележка (по избор, макс 500 знака)
              </label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value.slice(0, 500) })}
                placeholder="напр. хистологично потвърдено 2024"
                data-testid="condition-notes"
                className={`w-full mt-1 px-2 py-1.5 rounded text-[12px] border ${
                  isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800"
                }`}
              />
            </div>
          </div>
          {errorMsg && (
            <div className={`text-[11px] mb-2 ${isDark ? "text-red-300" : "text-red-700"}`} data-testid="condition-error">
              {errorMsg}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              data-testid="condition-save-btn"
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-[12px] font-semibold ${
                saving ? "bg-slate-500 text-white cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700 text-white"
              }`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {saving ? "Записване..." : "Запази"}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setErrorMsg(null); }}
              className={`px-3 py-1.5 rounded text-[12px] ${
                isDark ? "bg-slate-700 text-slate-200" : "bg-slate-200 text-slate-700"
              }`}
            >
              Отказ
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-1.5 text-[12px] opacity-60">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Зареждане...
        </div>
      ) : items.length === 0 ? (
        <div className={`text-[12px] py-3 ${isDark ? "text-slate-400" : "text-slate-500"}`} data-testid="conditions-empty">
          Все още не сте добавили заболявания. Натиснете <b>Добави</b> за да въведете структурирани диагнози.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((c) => (
            <ConditionItem
              key={c.id}
              c={c}
              isDark={isDark}
              API={API}
              onRemove={remove}
              onConsolidate={consolidate}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// iter912 — Full audit-trace condition row: source badge + "View document"
// link + KEGG pathway chips. Preserves original delete / consolidate CTAs.
function ConditionItem({ c, isDark, API, onRemove, onConsolidate }) {
  const [expanded, setExpanded] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(false);

  // iter933 — Fixed silent failure: (1) `lab_analyses` docs no longer
  // persist the raw file (only `filename` + parsed analysis), so remove
  // them from the endpoint map — button hides instead of clicking to
  // nothing. (2) Real download errors now surface a toast.
  const src = SOURCE_META[c.source] || SOURCE_META.manual;
  const SourceIcon = src.icon;
  const hasTrace = c.source && c.source !== "manual";
  const SOURCE_TO_ENDPOINT = {
    epicrisis:    (id) => `/documents/epicrises/${id}/file`,
  };
  const sourceEndpoint = c.source_id && SOURCE_TO_ENDPOINT[c.source];
  const hasSourceDoc = !!sourceEndpoint;
  const pathwayCount = (c.matching_pathways || []).length;

  // Open the underlying source PDF/image in a new tab.
  const openSourceDoc = async () => {
    if (!hasSourceDoc || viewingDoc) return;
    setViewingDoc(true);
    try {
      const res = await axios.get(`${API}${sourceEndpoint(c.source_id)}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("Оригиналният файл вече не е достъпен в системата.");
      } else {
        toast.error("Не мога да отворя документа. Опитайте по-късно.");
      }
    } finally {
      setViewingDoc(false);
    }
  };

  const traceBadgeCls = {
    amber:   isDark ? "bg-amber-900/40 text-amber-200"   : "bg-amber-100 text-amber-800",
    sky:     isDark ? "bg-sky-900/40 text-sky-200"       : "bg-sky-100 text-sky-800",
    fuchsia: isDark ? "bg-fuchsia-900/40 text-fuchsia-200":"bg-fuchsia-100 text-fuchsia-800",
    blue:    isDark ? "bg-blue-900/40 text-blue-200"     : "bg-blue-100 text-blue-800",
    slate:   isDark ? "bg-slate-800 text-slate-400"      : "bg-slate-100 text-slate-600",
  }[src.color] || "bg-slate-100 text-slate-600";

  const refDateLabel = c.source_ref?.date ? fmtDate(c.source_ref.date) : "";
  const refFile      = c.source_ref?.filename || "";

  return (
    <div
      data-testid={`condition-item-${c.condition_id}`}
      className={`p-2.5 rounded-lg ${
        isDark ? "bg-slate-950/60 border border-slate-800" : "bg-slate-50 border border-slate-200"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className={`text-[13px] font-semibold flex items-center gap-1.5 flex-wrap ${isDark ? "text-slate-100" : "text-slate-900"}`}>
            {c.condition || c.condition_id}
            {c.origin === "onboarding" && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${isDark ? "bg-blue-900/60 text-blue-300" : "bg-blue-100 text-blue-700"}`}
                title="Добавена автоматично от онбординг wizard-a. Изтриването премахва и от там."
                data-testid={`condition-origin-badge-${c.condition_id}`}
              >
                📋 от онбординг
              </span>
            )}
            {c.origin === "manual" && c.source === "manual" && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${isDark ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-600"}`}
                title="Ръчно добавена диагноза"
              >
                ✋ ръчно
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-[10px] flex-wrap">
            <span className={`px-1.5 py-0.5 rounded font-semibold ${
              c.severity === "severe" || c.severity === "clinical"
                ? "bg-red-100 text-red-800"
                : c.severity === "moderate"
                  ? "bg-amber-100 text-amber-800"
                  : "bg-slate-200 text-slate-700"
            }`}>
              {SEVERITY_LABELS_BG[c.severity] || c.severity}
            </span>
            {c.diagnosed_at && (
              <span className={`${isDark ? "text-slate-400" : "text-slate-600"}`}>
                диагн. {c.diagnosed_at}
              </span>
            )}
            {c.notes && (
              <span className={`italic ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                · {c.notes.slice(0, 80)}{c.notes.length > 80 && "..."}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {c.origin === "onboarding" && (
            <button
              type="button"
              onClick={() => onConsolidate(c)}
              data-testid={`condition-consolidate-${c.condition_id}`}
              title="Потвърди с пълни детайли (severity, дата на диагноза, бележки)"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold ${isDark ? "bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}
            >
              <ShieldCheck className="w-3 h-3" />
              Потвърди
            </button>
          )}
          <button
            type="button"
            onClick={() => onRemove(c.id)}
            data-testid={`condition-delete-${c.condition_id}`}
            className={`p-1 rounded hover:bg-red-100 ${isDark ? "text-red-400 hover:bg-red-900/40" : "text-red-600"}`}
            aria-label="Изтрий"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* iter912 — Audit trace row (source + KEGG pathways) */}
      {(hasTrace || pathwayCount > 0) && (
        <div
          data-testid={`condition-audit-trace-${c.condition_id}`}
          className={`mt-2 pt-2 border-t flex flex-wrap items-center gap-1.5 text-[10px] ${
            isDark ? "border-slate-800" : "border-slate-200"
          }`}
        >
          {hasTrace && (
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-bold ${traceBadgeCls}`}
              data-testid={`condition-source-badge-${c.condition_id}`}
            >
              {SourceIcon && <SourceIcon className="w-3 h-3" />}
              {src.verb ? `${src.verb} ` : ""}{src.label}
              {refDateLabel ? ` · ${refDateLabel}` : ""}
            </span>
          )}
          {refFile && (
            <span
              className={`opacity-70 truncate max-w-[180px] ${isDark ? "text-slate-400" : "text-slate-600"}`}
              title={refFile}
            >
              {refFile}
            </span>
          )}
          {hasSourceDoc && (
            <button
              type="button"
              onClick={openSourceDoc}
              disabled={viewingDoc}
              data-testid={`condition-view-source-${c.condition_id}`}
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md font-semibold transition-colors ${
                isDark
                  ? "bg-amber-500/10 hover:bg-amber-500/20 text-amber-300"
                  : "bg-amber-50 hover:bg-amber-100 text-amber-700"
              } disabled:opacity-60`}
            >
              {viewingDoc ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <ExternalLink className="w-2.5 h-2.5" />}
              Виж документа
            </button>
          )}
          {pathwayCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`condition-toggle-pathways-${c.condition_id}`}
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md font-semibold transition-colors ${
                isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-slate-200 text-slate-700 hover:bg-slate-300"
              }`}
            >
              <ChevronDown className={`w-2.5 h-2.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
              {pathwayCount} KEGG {pathwayCount === 1 ? "път" : "пътища"}
            </button>
          )}
        </div>
      )}

      {expanded && pathwayCount > 0 && (
        <div
          className="mt-1.5 flex flex-wrap gap-1"
          data-testid={`condition-pathways-${c.condition_id}`}
        >
          {c.matching_pathways.map((p) => (
            <a
              key={p.id}
              href={p.kegg ? `https://www.kegg.jp/pathway/${p.kegg}` : undefined}
              target={p.kegg ? "_blank" : undefined}
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                isDark ? "bg-indigo-900/40 hover:bg-indigo-900/60 text-indigo-200" : "bg-indigo-50 hover:bg-indigo-100 text-indigo-700"
              }`}
              title={p.name_bg}
            >
              {p.name_bg}
              {p.kegg && <ExternalLink className="w-2 h-2 opacity-60" />}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
